/**
 * Color Grade Undo Store — dedicated undo/redo for color grading parameter changes.
 *
 * Uses zundo (zustand/temporal) middleware to maintain a separate undo stack
 * for color grade operations, independent from the timeline command store.
 *
 * Usage:
 *   1. Before each committed grade change, call `recordBeforeChange(effectsByItem)`
 *      with the CURRENT (pre-change) effects state.
 *   2. On Ctrl+Z, call `undoGrade()` which reads the restored state and applies it
 *      back to timeline items via the provided callback.
 *   3. On Ctrl+Shift+Z, call `redoGrade()` similarly.
 */

import { create } from 'zustand'
import { temporal } from 'zundo'
import type { ItemEffect } from '@/types/effects'
import { useItemsStore } from '@/features/timeline/stores/items-store'
import { useGizmoStore } from '@/features/preview/stores/gizmo-store'

interface ColorGradeUndoState {
  /** Current recorded effects state (itemId → effects array) */
  effectsByItem: Record<string, ItemEffect[]>
}

interface ColorGradeUndoActions {
  /**
   * Record the current effects BEFORE a grade change.
   * Call this with the effects as they currently exist in the timeline items.
   * Zundo automatically captures the previous state for undo.
   */
  recordBeforeChange: (effectsByItem: Record<string, ItemEffect[]>) => void

  /** Clear all undo/redo history (e.g., when switching items). */
  clearHistory: () => void
}

export const useColorGradeUndoStore = create<ColorGradeUndoState & ColorGradeUndoActions>()(
  temporal(
    (set) => ({
      effectsByItem: {},

      recordBeforeChange: (effectsByItem) => {
        set({ effectsByItem })
      },

      clearHistory: () => {
        // Clear both the store state and zundo's temporal past/future stacks
        useColorGradeUndoStore.temporal.getState().clear()
        set({ effectsByItem: {} })
      },
    }),
    {
      // Only track the effects state in history
      partialize: (state) => ({
        effectsByItem: state.effectsByItem,
      }),
    },
  ),
)

// ─── Convenience helpers ────────────────────────────────────────────────

/** Check if the color grade undo stack has entries to undo. */
export function getGradeCanUndo(): boolean {
  return useColorGradeUndoStore.temporal.getState().pastStates.length > 0
}

/** Check if the color grade redo stack has entries to redo. */
export function getGradeCanRedo(): boolean {
  return useColorGradeUndoStore.temporal.getState().futureStates.length > 0
}

/**
 * Apply a restored effects map back to timeline items and gizmo preview.
 * Bypasses the timeline command store to avoid circular undo entries.
 */
export function applyRestoredEffects(effectsByItem: Record<string, ItemEffect[]>): void {
  const items = useItemsStore.getState().items
  let changed = false
  const nextItems = items.map((item) => {
    const restored = effectsByItem[item.id]
    if (!restored) return item
    changed = true
    return { ...item, effects: restored }
  })
  if (!changed) return
  useItemsStore.getState().setItems(nextItems)

  // Update gizmo preview to match restored effects
  const effectsPreview: Record<string, ItemEffect[]> = {}
  for (const [itemId, effects] of Object.entries(effectsByItem)) {
    effectsPreview[itemId] = effects
  }
  useGizmoStore.getState().setEffectsPreviewNew(effectsPreview)
}

/**
 * Perform a color grade undo.
 * Zundo restores the previous `effectsByItem` state; the caller is responsible
 * for writing the restored effects back to the timeline items.
 *
 * @returns The restored effectsByItem map, or null if nothing to undo.
 */
export function undoGrade(): Record<string, ItemEffect[]> | null {
  const temporalState = useColorGradeUndoStore.temporal.getState()
  if (temporalState.pastStates.length === 0) return null

  temporalState.undo()
  return useColorGradeUndoStore.getState().effectsByItem
}

/**
 * Perform a color grade redo.
 * Zundo restores the next `effectsByItem` state; the caller is responsible
 * for writing the restored effects back to the timeline items.
 *
 * @returns The restored effectsByItem map, or null if nothing to redo.
 */
export function redoGrade(): Record<string, ItemEffect[]> | null {
  const temporalState = useColorGradeUndoStore.temporal.getState()
  if (temporalState.futureStates.length === 0) return null

  temporalState.redo()
  return useColorGradeUndoStore.getState().effectsByItem
}
